// RegisterTree — collapsible tree view of risk registers.
// Used in the T1 (Tree + List) layout as a content-area sidebar.
// Fetches the register tree from the API and builds a client-side hierarchy.

import { useState, useCallback, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { registersApi, buildRegisterTree, type RegisterTreeNode } from '@/lib/api/registers'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { ChevronRight, ChevronDown, FolderOpen, Folder, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'

interface RegisterTreeProps {
  selectedRegisterId: string | null
  onSelectRegister: (registerId: string, registerName: string) => void
  onCreateSubRegister?: (parentId: string) => void
}

export function RegisterTree({
  selectedRegisterId,
  onSelectRegister,
  onCreateSubRegister,
}: RegisterTreeProps) {
  const api = useApi()
  const { slug } = useTenant()
  const regApi = registersApi(api)

  const {
    data: registers,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.registers.tree(slug),
    queryFn: () => regApi.tree(),
  })

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const hasAutoExpanded = useRef(false)

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  // Auto-expand root-level nodes on first load
  const tree = registers ? buildRegisterTree(registers) : []

  useEffect(() => {
    if (tree.length > 0 && !hasAutoExpanded.current) {
      hasAutoExpanded.current = true
      setExpandedIds(new Set(tree.map((node) => node.id)))
    }
  }, [registers]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <div className="space-y-2 p-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-7 animate-pulse rounded bg-muted/50" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-3">
        <ErrorState
          message="Failed to load registers"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  if (tree.length === 0) {
    return <div className="p-4 text-center text-sm text-muted-foreground">No registers found.</div>
  }

  return (
    <div className="py-1">
      {tree.map((node) => (
        <TreeNode
          key={node.id}
          node={node}
          depth={0}
          expandedIds={expandedIds}
          selectedId={selectedRegisterId}
          onToggle={toggleExpand}
          onSelect={onSelectRegister}
          onCreateSub={onCreateSubRegister}
        />
      ))}
    </div>
  )
}

// --- Internal tree node ---

interface TreeNodeProps {
  node: RegisterTreeNode
  depth: number
  expandedIds: Set<string>
  selectedId: string | null
  onToggle: (id: string) => void
  onSelect: (id: string, name: string) => void
  onCreateSub?: (parentId: string) => void
}

function TreeNode({
  node,
  depth,
  expandedIds,
  selectedId,
  onToggle,
  onSelect,
  onCreateSub,
}: TreeNodeProps) {
  const isExpanded = expandedIds.has(node.id)
  const isSelected = node.id === selectedId
  const hasChildren = node.has_children || node.children.length > 0
  const paddingLeft = 8 + depth * 16

  return (
    <div>
      <div
        className={cn(
          'group mx-1 flex cursor-pointer items-center gap-1 rounded-sm px-2 py-1 text-sm',
          'transition-colors hover:bg-accent/50',
          isSelected && 'bg-accent font-medium text-accent-foreground',
        )}
        style={{ paddingLeft }}
        onClick={() => onSelect(node.id, node.name)}
      >
        {/* Expand/collapse toggle */}
        <button
          className={cn(
            'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-sm',
            'hover:bg-accent',
            !hasChildren && 'invisible',
          )}
          onClick={(e) => {
            e.stopPropagation()
            onToggle(node.id)
          }}
        >
          {hasChildren &&
            (isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            ))}
        </button>

        {/* Folder icon */}
        {isExpanded && hasChildren ? (
          <FolderOpen className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        ) : (
          <Folder className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        )}

        {/* Label */}
        <span className="flex-1 truncate" title={node.name}>
          {node.name}
        </span>

        {/* Risk count badge */}
        {node.risk_count > 0 && (
          <span className="flex-shrink-0 text-xs tabular-nums text-muted-foreground">
            {node.risk_count}
          </span>
        )}

        {/* Add sub-register button (shown on hover) */}
        {onCreateSub && (
          <button
            className="flex-shrink-0 rounded p-0.5 opacity-0 transition-opacity hover:bg-accent group-hover:opacity-100"
            title="Create sub-register"
            onClick={(e) => {
              e.stopPropagation()
              onCreateSub(node.id)
            }}
          >
            <Plus className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Children */}
      {isExpanded && node.children.length > 0 && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              selectedId={selectedId}
              onToggle={onToggle}
              onSelect={onSelect}
              onCreateSub={onCreateSub}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export { type RegisterTreeNode }
